<ion-page>
  <ion-content class="fade-slide-enter">
    <div class="page-shell">
      <div class="page-content">
        <div class="layout">
          <app-back-button></app-back-button>

          <div class="page-header">
            <h1 class="page-title">Privacy Policy</h1>
            <p class="subtitle">Last updated: December 2025</p>
          </div>

          <div class="glass-card terms-card">
            <section>
              <p>
                Miguel Bautista Pérez<br />
                José Bergamín 40, Madrid, Spain<br />
                Email: <a href="mailto:support@tubify.eu">support@tubify.eu</a>
              </p>
              <p>
                Tubify (“Tubify”, “we”, “us”, “our”) is committed to protecting your privacy. This Privacy Policy explains:
              </p>
              <ul>
                <li>What information we collect</li>
                <li>How and why we use it</li>
                <li>How long we keep it</li>
                <li>With whom we share it</li>
                <li>How we comply with the EU GDPR</li>
                <li>How we comply with YouTube API Services Terms of Service, the Google API Services User Data Policy (including Limited Use requirements), and Spotify Developer Terms</li>
                <li>Your rights and choices, including consent, cookies, and data deletion</li>
              </ul>
              <p>By using Tubify, you acknowledge that you have read and understood this Privacy Policy.</p>
            </section>

            <section>
              <h3>1. What Tubify Is</h3>
              <p>
                Tubify is a playlist-conversion service that enables users to recreate their personal playlists between Spotify and YouTube / YouTube Music.
                Tubify only processes the metadata needed to perform playlist conversion.</p>
              <p> 
                Tubify does not:
              </p>
              <ul>
                <li>Download, store, or redistribute audiovisual content</li>
                <li>Provide any download functionality for YouTube videos or Spotify audio</li>
              </ul>
            </section>

            <section>
              <h3>2. Data Controller</h3>
              <p>The Data Controller responsible for processing your personal data is:</p>
              <p>
                Miguel Bautista Pérez<br />
                Address: José Bergamín 40, Madrid, Spain<br />
                Email: <a href="mailto:support@tubify.eu">support@tubify.eu</a>
              </p>
              <p>You may contact us at any time regarding this Privacy Policy or your rights.</p>
            </section>

            <section>
              <h3>3. Categories of Data We Collect</h3>
              <p>We collect the minimum amount of data necessary to operate Tubify and provide the Service.</p>

              <h4>3.1 Account Information</h4>
              <p>When you create a Tubify account, we process:</p>
              <ul>
                <li>Email address</li>
                <li>Password (hashed and salted; we never store raw passwords)</li>
                <li>Premium subscription status (e.g., free, monthly, yearly, lifetime)</li>
                <li>Account creation and login timestamps</li>
                <li>Basic usage metadata (e.g., last login date)</li>
              </ul>
              <p>Purpose: Account creation, login, access control, premium feature management, and security.</p>

              <h4>3.2 OAuth Authorization Data (YouTube &amp; Spotify)</h4>
              <p>When you connect your YouTube or Spotify account to Tubify, we receive:</p>
              <ul>
                <li>Access token</li>
                <li>Refresh token</li>
                <li>Token expiration time</li>
                <li>Scopes granted</li>
              </ul>
              <p>These tokens are used only to perform the actions you request, encrypted at rest, and stored on secure servers in the EU (or in jurisdictions with adequate safeguards, see Section 13).</p>
              <p>You can revoke access at any time:</p>
              <ul>
                <li><a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener">YouTube / Google</a></li>
                <li><a href="https://www.spotify.com/account/apps/" target="_blank" rel="noopener">Spotify</a></li>
              </ul>
              <p>After revocation, we can no longer access your accounts, and related OAuth tokens are deleted (see Sections 9 and 11).</p>

              <h4>3.3 YouTube API Data We Access</h4>
              <p>When you use Tubify with YouTube or YouTube Music, we access only the metadata required to find and create playlist matches:</p>
              <ul>
                <li>Video titles</li>
                <li>Channel names</li>
                <li>Video durations</li>
                <li>Video IDs</li>
                <li>Playlist metadata (playlist IDs, playlist names, number of items)</li>
              </ul>
              <p>We do not access or store video audio/streams, comments, user messages, or YouTube user passwords.</p>
              <h4>3.3.1 YouTube Data We Temporarily Store</h4>
              <ol>
                <li>
                  <strong>Cached match results</strong><br />
                  - Mapping: Spotify track ID → YouTube video ID<br />
                  - Track title and artist name (normalized)<br />
                  - Match confidence score<br />
                  - Timestamp of when the match was computed
                </li>
                <li>
                  <strong>Conversion snapshots</strong> (for showing you the results)<br />
                  - Source track titles and artists<br />
                  - Matched YouTube video titles, channel names, and durations<br />
                  - Candidate match list (if applicable)<br />
                  - Conversion status and progress<br />
                  - Resulting YouTube playlist ID or URL
                </li>
              </ol>
              <p>We do not store thumbnails, descriptions, statistics, comments, or any audiovisual content.</p>

              <h4>3.4 Spotify API Data We Access</h4>
              <p>When you use Tubify with Spotify, we access:</p>
              <ul>
                <li>Playlist titles and playlist IDs</li>
                <li>Track titles</li>
                <li>Artist names</li>
                <li>Track durations</li>
                <li>ISRC codes (where available)</li>
              </ul>
              <p>We do not access or store Spotify audio streams or your Spotify password.</p>
              <h4>3.4.1 Spotify Data We Temporarily Store</h4>
              <ul>
                <li>Normalized track titles and artist names</li>
                <li>Playlist IDs and titles for reference in conversions</li>
                <li>Mapping: Spotify track ID ↔ YouTube video ID</li>
                <li>Conversion status, processed track count, and result playlist IDs</li>
              </ul>
              <p>We do not use Spotify data for advertising or marketing purposes and do not transfer it to advertisers.</p>

              <h4>3.5 Usage and Log Data</h4>
              <p>When you use Tubify, we may automatically collect:</p>
              <ul>
                <li>IP address (truncated or anonymized where possible)</li>
                <li>Browser type and version</li>
                <li>Device type and operating system</li>
                <li>Timestamp and duration of sessions</li>
                <li>API request logs (e.g., error codes, quota information)</li>
              </ul>
              <p>Purpose: Security, fraud prevention, abuse detection, performance monitoring, and troubleshooting.</p>

              <h4>3.6 Advertising and Analytics Data</h4>
              <p>On the free version of Tubify, we display advertisements through third-party advertising providers. This may involve:</p>
              <ul>
                <li>Cookies or similar technologies placed by third-party ad providers</li>
                <li>Anonymized or pseudonymous identifiers for ad delivery and frequency capping</li>
                <li>Basic information about your browser, device, and approximate location</li>
              </ul>
              <p>We do not share YouTube API Data or Spotify Data with third-party ad providers. See Section 8 for choices.</p>
            </section>

            <section>
              <h3>4. Purposes and Legal Bases for Processing (GDPR)</h3>
              <h4>4.1 Performance of a Contract (Art. 6(1)(b) GDPR)</h4>
              <p>We process data necessary to provide the Service you request, including account access, OAuth connection, playlist retrieval, matching, creation, caching, and support.</p>
              <h4>4.2 Consent (Art. 6(1)(a) GDPR)</h4>
              <p>Used for OAuth authorization, non-essential cookies (including third-party ads), and optional analytics. You may withdraw consent at any time via OAuth revocation or cookie settings.</p>
              <h4>4.3 Legitimate Interests (Art. 6(1)(f) GDPR)</h4>
              <p>Security and integrity of the Service, minimal caching to improve performance, and internal analytics where not based on consent. We perform balancing tests where required.</p>
              <h4>4.4 Legal Obligations (Art. 6(1)(c) GDPR)</h4>
              <p>Compliance with accounting/tax rules, lawful authority requests, and protecting/enforcing legal claims.</p>
            </section>

            <section>
              <h3>5. How We Use Your Data</h3>
              <p>We use your data to:</p>
              <ul>
                <li>Authenticate you and maintain your Tubify account</li>
                <li>Connect to your Spotify and/or YouTube account with your permission</li>
                <li>Retrieve the playlists you select and analyze their metadata</li>
                <li>Find equivalent tracks on the target platform (Spotify ↔ YouTube)</li>
                <li>Create or modify playlists on the target platform according to your instructions</li>
                <li>Display conversion progress and results</li>
                <li>Improve match quality and reduce redundant API calls through minimal caching</li>
                <li>Monitor and protect the Service against abuse and unauthorized access</li>
                <li>Provide customer support</li>
                <li>Comply with platform terms (YouTube, Google, Spotify) and applicable law</li>
              </ul>
              <p>We do not sell personal data or monetize YouTube API Data or Spotify Data, and we do not share OAuth tokens with unrelated third parties.</p>
            </section>

            <section>
              <h3>6. Use of Data from Google APIs (YouTube API Services)</h3>
              <h4>6.1 Compliance with Google API Services User Data Policy and Limited Use</h4>
              <ul>
                <li>We only use data obtained from Google APIs to provide or improve user-facing features of Tubify.</li>
                <li>We do not use Google API Data for advertising purposes.</li>
                <li>We do not sell Google API Data.</li>
                <li>We do not transfer Google API Data to third parties except as necessary to provide or improve Tubify, comply with law, or as part of a transaction with appropriate protections.</li>
                <li>We do not permit humans to read Google API Data except with consent, for security/fraud investigation, when required by law, or for debugging under strict controls.</li>
              </ul>
              <h4>6.2 Relationship with YouTube and Google</h4>
              <p>By using Tubify, you also agree to the following:</p>
              <ul>
                <li>YouTube Terms of Service - <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener">https://www.youtube.com/t/terms</a></li>
                <li>Google Privacy Policy - <a href="https://policies.google.com/privacy" target="_blank" rel="noopener">https://policies.google.com/privacy</a></li>
              </ul>
              <p>Google’s processing of your data via YouTube API Services is governed by the Google Privacy Policy. Tubify is independent and not endorsed, sponsored, or certified by Google LLC or YouTube.</p>
            </section>

            <section>
              <h3>7. Use of Spotify Data</h3>
              <p>Tubify uses the Spotify Web API to retrieve playlist and track metadata and to create playlists on your Spotify account.</p>
              <ul>
                <li>We use Spotify metadata solely for playlist conversion and related user-facing features.</li>
                <li>We do not use Spotify Data for advertising, targeting, or marketing.</li>
                <li>We do not sell Spotify Data or transfer it to advertisers or unrelated third parties.</li>
                <li>Any storage of Spotify-related metadata is temporary and limited to what is necessary to perform conversions and improve match quality.</li>
              </ul>
              <p>By using Tubify, you also agree to the following:</p>
              <ul>
                <li>Spotify Terms and Conditions - <a href="https://www.spotify.com/legal/end-user-agreement/" target="_blank" rel="noopener">https://www.spotify.com/legal/end-user-agreement</a></li>
                <li>Spotify Privacy Policy - <a href="https://www.spotify.com/legal/privacy-policy/" target="_blank" rel="noopener">https://www.spotify.com/legal/privacy-policy</a></li>
              </ul>
              <p>Tubify is not endorsed or sponsored by Spotify AB.</p>
            </section>

            <section>
              <h3>8. Cookies and Tracking Technologies</h3>
              <h4>8.1 Types of Cookies</h4>
              <ul>
                <li><strong>Essential (strictly necessary) cookies</strong>: authentication, session, security (CSRF), basic functionality. Legal basis: legitimate interest or performance of a contract. Not optional via the banner.</li>
                <li><strong>Analytics cookies</strong>: aggregated, anonymized or pseudonymized analytics. Legal basis: consent.</li>
                <li><strong>Advertising cookies (third-party ad providers)</strong>: for ad delivery, frequency capping, and fraud prevention. Legal basis: consent. We do not share YouTube API Data or Spotify Data with third-party ad providers.</li>
              </ul>
              <h4>8.2 Cookie Banner and Consent Management</h4>
              <p>Users see a banner to accept/reject non-essential cookies and can change choices via “Cookie Settings.” Disabling non-essential cookies may limit ads/analytics but essential functionality remains.</p>
            </section>

            <section>
              <h3>9. Data Retention</h3>
              <ul>
                <li><strong>OAuth tokens (YouTube &amp; Spotify):</strong> stored until you revoke access, delete your account, 90+ days of inactivity, or no longer needed. Removed from active systems within a reasonable period (typically within 30 days), subject to backups.</li>
                <li><strong>Cached match results and conversion snapshots:</strong> retained up to 90 days from last relevant use, then deleted or anonymized; used only to improve performance and match quality.</li>
                <li><strong>Account information:</strong> retained for the lifetime of your account; deleted or anonymized within a reasonable period after deletion, except where law requires longer retention.</li>
                <li><strong>Log and security data:</strong> retained up to 180 days unless needed longer for security, fraud investigation, or legal obligations.</li>
                <li><strong>Billing/financial data (if applicable):</strong> retained per accounting/tax laws (usually 5–10 years).</li>
              </ul>
              <p>Backups may contain data for a limited additional period and are accessed only for recovery or security investigations.</p>
            </section>

            <section>
              <h3>10. Your Rights Under GDPR</h3>
              <p>As an individual in the EU/EEA (and where applicable, the UK or similar jurisdictions), you have the right to:</p>
              <ul>
                <li>Access</li>
                <li>Rectification</li>
                <li>Erasure (“right to be forgotten”)</li>
                <li>Restriction of processing</li>
                <li>Data portability</li>
                <li>Object to processing based on legitimate interests or direct marketing</li>
                <li>Withdraw consent at any time (e.g., cookies, OAuth access)</li>
                <li>Lodge a complaint with your local data protection authority</li>
              </ul>
              <p>To exercise your rights, contact <a href="mailto:support@tubify.eu">support@tubify.eu</a>. We may need to verify your identity.</p>
            </section>

            <section>
              <h3>11. Revoking Access to YouTube and Spotify</h3>
              <p>You may revoke Tubify’s access at any time:</p>
              <ul>
                <li><a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener">YouTube / Google</a></li>
                <li><a href="https://www.spotify.com/account/apps/" target="_blank" rel="noopener">Spotify</a></li>
              </ul>
              <p>
                After revocation:
                <br />- Tubify is immediately prevented from making further API requests.
                <br />- Related OAuth tokens are removed within a reasonable period (typically within 30 days), subject to backups.
                <br />- Remaining cached metadata follows Section 9.
              </p>
              <p>Revocation may disable some or all features of Tubify.</p>
            </section>

            <section>
              <h3>12. Children’s Privacy</h3>
              <p>Tubify is not intended for users under 16 years of age. We do not knowingly collect personal data from children under 16. If you believe a child has provided data, contact <a href="mailto:support@tubify.eu">support@tubify.eu</a> and we will delete it.</p>
            </section>

            <section>
              <h3>13. International Data Transfers</h3>
              <p>We primarily use EU-based infrastructure, but data may be processed outside the EU/EEA (e.g., via hosting providers or processors). Where transfers occur, we use safeguards such as:</p>
              <ul>
                <li>EU–US Data Privacy Framework (where applicable)</li>
                <li>Standard Contractual Clauses (SCCs)</li>
                <li>Adequacy decisions by the European Commission</li>
              </ul>
              <p>Contact us for details on specific transfer mechanisms.</p>
            </section>

            <section>
              <h3>14. Data Sharing and Processors</h3>
              <p>We do not sell or rent your personal data. We may share data with:</p>
              <ul>
                <li>YouTube API Services (Google LLC) – only to perform operations you request.</li>
                <li>Spotify Web API (Spotify AB) – only to retrieve and recreate your playlists as requested.</li>
                <li>Hosting/infrastructure providers (e.g., Hetzner, Vercel) – to run the application securely.</li>
                <li>Advertising networks (third-party) – for ads in the free version (no sharing of YouTube API Data or Spotify Data).</li>
                <li>Analytics providers (if used) – aggregated/pseudonymized and only with consent where required.</li>
                <li>Professional advisors – under confidentiality, for legal/compliance reasons.</li>
                <li>Authorities – where required by law or to protect our rights.</li>
              </ul>
              <p>Any processors with potential access to YouTube API Data are bound by obligations consistent with the Google API Services User Data Policy (including Limited Use) and may not use data for other purposes.</p>
            </section>

            <section>
              <h3>15. Security Measures</h3>
              <p>We take technical and organizational measures to protect your data, including:</p>
              <ul>
                <li>Encryption of OAuth tokens at rest (e.g., AES-256 or equivalent)</li>
                <li>HTTPS for all data in transit</li>
                <li>Access controls and role-based permissions</li>
                <li>Rate-limiting and abuse detection</li>
                <li>Regular security updates and monitoring</li>
                <li>EU-based hosting infrastructure (where feasible)</li>
              </ul>
              <p>No system is completely secure; we will notify you and authorities of breaches as required by GDPR.</p>
            </section>

            <section>
              <h3>16. Automated Decision-Making and Profiling</h3>
              <p>
                Tubify uses automated processes to analyze playlist data, compute match confidence scores, and suggest candidate matches. These processes do not produce legal or similarly significant effects on you, and we do not use your personal data for profiling within the meaning of GDPR Article 22.
              </p>
            </section>

            <section>
              <h3>17. Relationship with YouTube and Spotify (Summary)</h3>
              <ul>
                <li>Using Tubify with YouTube binds you to YouTube Terms of Service and the Google Privacy Policy.</li>
                <li>Using Tubify with Spotify binds you to Spotify Terms and Conditions of Use and the Spotify Privacy Policy.</li>
                <li>Tubify is not affiliated with, endorsed, or sponsored by Google LLC, YouTube, or Spotify AB.</li>
              </ul>
            </section>

            <section>
              <h3>18. Changes to This Privacy Policy</h3>
              <p>We may update this Privacy Policy over time. For material changes, we will notify you by email and/or in-app and update the “Last updated” date. Where required by law, we will seek your consent for changes affecting consent-based processing.</p>
            </section>

            <section>
              <h3>19. Contact and Complaints</h3>
              <p>
                If you have questions, concerns, or requests about this Policy or our data processing, contact:<br />
                Data Controller: Miguel Bautista Pérez<br />
                Address: José Bergamín 40, Madrid, Spain<br />
                Email: <a href="mailto:support@tubify.eu">support@tubify.eu</a>
              </p>
              <p>You also have the right to lodge a complaint with your local data protection authority.</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  </ion-content>
</ion-page>
